import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { connectSocket } from '../../utils/socket';
import QUIZ_THEMES from '../../utils/quizThemes';
import { sound } from '../../utils/sound';

const OPTION_KEYS = ['a', 'b', 'c', 'd'];

export default function QuizPage() {
  const { sessionId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(() => i18n.language === 'al' ? 'al' : 'de');

  const toggleLang = () => {
    const next = lang === 'de' ? 'al' : 'de';
    setLang(next);
    i18n.changeLanguage(next);
  };

  const socketRef = useRef(null);
  const recapRef = useRef([]);
  const timerRef = useRef(null);
  const soundRef = useRef(state?.quiz?.soundParticipant === true);

  // Feature flags from navigation state
  const enable5050 = state?.enable5050 ?? false;
  const enableStreak = state?.enableStreak ?? false;

  const [question, setQuestion] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [qTotal, setQTotal] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLimitSec, setTimeLimitSec] = useState(20);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(null); // { isCorrect, scoreEarned, correctOption }
  const [awaitingDelayed, setAwaitingDelayed] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [hiddenOptions, setHiddenOptions] = useState([]);
  const [canUse5050, setCanUse5050] = useState(enable5050);
  const [phase, setPhase] = useState('question'); // question | leaderboard

  const participant = state?.participant;
  const bgImage = state?.bgImage || null;
  const baseTheme = QUIZ_THEMES[state?.bgColor] || QUIZ_THEMES.dark;
  const customColor = state?.customColor || null;
  const theme = customColor ? { ...baseTheme, accent: customColor } : baseTheme;
  const outerBg = bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: theme.bg };

  const handleAnswer = useCallback((option) => {
    if (selected || revealed || awaitingDelayed) return;
    setSelected(option);
    if (timerRef.current) clearInterval(timerRef.current);

    const responseTimeMs = (timeLimitSec - timeLeft) * 1000;
    socketRef.current?.emit('competitor:answer', {
      sessionId,
      questionId: question.id,
      selectedOption: option,
      responseTimeMs: Math.min(responseTimeMs, timeLimitSec * 1000),
    });
  }, [selected, revealed, awaitingDelayed, timeLimitSec, timeLeft, sessionId, question]);

  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    // Rilidhje graceful: ri-regjistro pjesemarresin ne (ri)lidhje; serveri e ruan score-n nga DB
    const rejoin = () => {
      const p = JSON.parse(localStorage.getItem('zq_participant') || '{}');
      socket.emit('competitor:join', {
        sessionId,
        firstName: p.firstName,
        lastName: p.lastName,
        language: p.language || localStorage.getItem('zq_lang') || 'de',
        ageGroupLabel: p.ageGroupLabel,
        team: p.team || null,
      });
    };
    socket.on('connect', rejoin);
    if (socket.connected) rejoin();

    socket.on('question:start', ({ index, total, timeLimitSec: tl, question: q }) => {
      setQuestion(q);
      recapRef.current.push({ q, correctOption: null });
      setQIndex(index);
      setQTotal(total);
      setTimeLimitSec(tl);
      setTimeLeft(tl);
      setSelected(null);
      setRevealed(null);
      setAwaitingDelayed(false);
      setHiddenOptions([]);
      setPhase('question');
      if (soundRef.current) sound.start();

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    });

    socket.on('answer:result', (data) => {
      // Delayed mode confirmation — show "Gesendet..." until question:end
      if (data.delayed === true) {
        setAwaitingDelayed(true);
        return;
      }
      // Real result (immediate or revealed after question:end)
      setAwaitingDelayed(false);
      setRevealed({ isCorrect: data.isCorrect, scoreEarned: data.scoreEarned, correctOption: data.correctOption });
      if (data.isCorrect) setScore(prev => prev + (data.scoreEarned || 0));
      if (enableStreak) setStreak(data.streak ?? 0);
    });

    socket.on('question:end', ({ correctOption, leaderboard: lb, delayed }) => {
      setLeaderboard(lb);
      if (soundRef.current) sound.reveal();
      const _r = recapRef.current; if (_r.length) _r[_r.length - 1].correctOption = correctOption;
      if (state?.quiz?.leaderboardParticipant !== false) setPhase('leaderboard');
      if (!delayed) {
        // Immediate mode: reveal correct answer for those who didn't answer
        setRevealed(prev => prev || { isCorrect: false, scoreEarned: 0, correctOption });
      }
      // In delayed mode: leave revealed=null until answer:result arrives
    });

    socket.on('5050:result', ({ hideOptions }) => {
      setHiddenOptions(hideOptions);
    });

    socket.on('quiz:finished', ({ leaderboard: lb, familyLeaderboard, teamScores, enableFamilyMode, isDuel }) => {
      navigate(`/results/${sessionId}`, {
        state: {
          leaderboard: lb,
          familyLeaderboard: familyLeaderboard || null,
          teamScores: teamScores || null,
          enableFamilyMode: enableFamilyMode || false,
          isDuel: isDuel || false,
          score,
          participant,
          participantId: state?.participantId || null,
          bgColor: state?.bgColor,
          bgImage: state?.bgImage,
          recap: recapRef.current,
          recapEnabled: state?.quiz?.recapEnabled || false,
        },
      });
    });

    return () => {
      socket.off('question:start');
      socket.off('answer:result');
      socket.off('question:end');
      socket.off('5050:result');
      socket.off('quiz:finished');
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId, navigate, score, participant, enableStreak]);

  const use5050 = () => {
    if (!canUse5050 || selected || awaitingDelayed) return;
    setCanUse5050(false);
    socketRef.current?.emit('competitor:use_5050', { sessionId });
  };

  const timerPct = timeLimitSec > 0 ? (timeLeft / timeLimitSec) * 100 : 0;
  const liveText = bgImage ? '#fff' : theme.text;

  if (!question) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', ...outerBg }}>
        <div className="spinner" />
      </div>
    );
  }

  const getText = (field) => question[`${field}_${lang}`] || question[`${field}_de`] || '';

  const overlayStyle = bgImage
    ? { minHeight: '100vh', background: 'rgba(0,0,0,0.65)', padding: '24px 16px', color: '#fff' }
    : { minHeight: '100vh', padding: '24px 16px', color: theme.text };

  const cardBg     = bgImage ? 'rgba(0,0,0,0.65)' : theme.surface;
  const cardBorder = bgImage ? 'rgba(255,255,255,0.18)' : theme.border;
  const optBg      = bgImage ? 'rgba(0,0,0,0.55)' : theme.surface;
  const optBorder  = bgImage ? 'rgba(255,255,255,0.28)' : theme.border;

  return (
    <div style={{ minHeight: '100vh', ...outerBg }}>
      <div style={overlayStyle}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: `${liveText}99` }}>
              {t('quiz.question')} {qIndex + 1} / {qTotal}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {enableStreak && streak > 0 && (
                <div style={{ background: `${theme.accent}33`, color: theme.accent, padding: '3px 10px', borderRadius: 100, fontSize: 13, fontWeight: 700 }}>
                  {streak}x
                </div>
              )}
              <div style={{ fontWeight: 700, color: theme.accent, fontSize: 16 }}>{score} Pkt.</div>
              <button
                onClick={toggleLang}
                style={{
                  background: bgImage ? 'rgba(255,255,255,0.15)' : theme.surface,
                  border: `1px solid ${bgImage ? 'rgba(255,255,255,0.25)' : theme.border}`,
                  color: liveText, borderRadius: 8, padding: '4px 10px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5,
                }}
              >
                {lang === 'de' ? 'DE' : 'AL'}
              </button>
            </div>
          </div>

          {/* Timer */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: `${liveText}99` }}>{t('common.time')}</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: timerPct < 30 ? '#E05C4B' : liveText }}>
                {timeLeft}s
              </span>
            </div>
            <div style={{ height: 8, background: bgImage ? 'rgba(255,255,255,0.2)' : theme.surface, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 4, transition: 'width 1s linear, background 0.3s',
                width: `${timerPct}%`,
                background: timerPct < 30 ? '#E05C4B' : timerPct < 50 ? '#C9A84C' : theme.accent,
              }} />
            </div>
          </div>

          {/* Question */}
          <div style={{
            background: cardBg,
            borderRadius: 16, padding: '24px 20px', marginBottom: 20, textAlign: 'center',
            border: `1px solid ${cardBorder}`,
          }}>
            {question.image_path && (
              <img
                src={`/uploads/${question.image_path}`}
                alt=""
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, marginBottom: 14, objectFit: 'contain' }}
              />
            )}
            <p style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4, color: bgImage ? '#fff' : liveText }}>
              {getText('text')}
            </p>
          </div>

          {/* Options */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {OPTION_KEYS.filter(k => question[`option_${k}_de`]).map(key => {
              if (hiddenOptions.includes(key)) return <div key={key} />;

              const isRevealed = revealed && key === revealed.correctOption;
              const isWrong = revealed && key === selected && !revealed.isCorrect;
              const isSelected = !revealed && key === selected;

              return (
                <button
                  key={key}
                  onClick={() => handleAnswer(key)}
                  disabled={!!selected || !!revealed || awaitingDelayed}
                  style={{
                    padding: '16px 14px',
                    borderRadius: 12,
                    border: `2px solid ${isRevealed ? '#5B8A75' : isWrong ? '#E05C4B' : isSelected ? theme.accent : optBorder}`,
                    background: isRevealed ? '#5B8A75' : isWrong ? '#E05C4B33' : isSelected ? `${theme.accent}44` : optBg,
                    color: bgImage ? '#fff' : (isRevealed ? '#fff' : liveText),
                    cursor: selected || revealed || awaitingDelayed ? 'default' : 'pointer',
                    opacity: revealed && !isRevealed && key !== selected ? 0.45 : 1,
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  <span style={{ display: 'block', fontSize: 11, color: isRevealed ? 'rgba(255,255,255,0.75)' : (bgImage ? 'rgba(255,255,255,0.55)' : `${liveText}77`), marginBottom: 4 }}>
                    {key.toUpperCase()}
                  </span>
                  {getText(`option_${key}`)}
                </button>
              );
            })}
          </div>

          {/* Delayed mode: "Gesendet..." */}
          {awaitingDelayed && !revealed && (
            <div style={{
              textAlign: 'center', fontSize: 15, padding: '14px 16px', borderRadius: 12, marginBottom: 12,
              background: cardBg, color: bgImage ? '#fff' : liveText, border: `1px solid ${cardBorder}`,
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Check size={16} />{t('quiz.answerSent') || 'Antwort gesendet — Ergebnis wird bald angezeigt...'}
              </span>
            </div>
          )}

          {/* Result feedback */}
          {revealed && (
            <div style={{
              textAlign: 'center', fontSize: 18, fontWeight: 700, padding: '16px',
              borderRadius: 12, marginBottom: 12,
              background: revealed.isCorrect ? '#5B8A7533' : '#E05C4B33',
              color: revealed.isCorrect ? (bgImage ? '#7FC9A9' : '#5B8A75') : '#E05C4B',
              border: `1px solid ${revealed.isCorrect ? '#5B8A75' : '#E05C4B'}66`,
            }}>
              {revealed.isCorrect
                ? `${t('common.correct')} +${revealed.scoreEarned} ${t('common.points')}`
                : t('common.wrong')
              }
            </div>
          )}

          {/* 50/50 — only show if enabled for this quiz */}
          {enable5050 && canUse5050 && !selected && !revealed && !awaitingDelayed && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                onClick={use5050}
                style={{
                  background: cardBg, border: `1px solid ${cardBorder}`,
                  color: bgImage ? '#fff' : liveText, padding: '8px 20px', borderRadius: 8,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                50/50
              </button>
            </div>
          )}

          {/* Leaderboard preview */}
          {phase === 'leaderboard' && leaderboard.length > 0 && (
            <div style={{ marginTop: 20, background: cardBg, borderRadius: 14, padding: 16, border: `1px solid ${cardBorder}` }}>
              <h3 style={{ fontSize: 14, color: bgImage ? 'rgba(255,255,255,0.6)' : `${liveText}99`, marginBottom: 12 }}>{t('quiz.leaderboard')}</h3>
              {leaderboard.slice(0, 5).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 4 ? `1px solid ${bgImage ? 'rgba(255,255,255,0.1)' : theme.border}` : 'none' }}>
                  <div style={{ width: 24, textAlign: 'center', fontWeight: 700, color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : (bgImage ? 'rgba(255,255,255,0.35)' : `${liveText}66`) }}>
                    {p.rank}
                  </div>
                  <div style={{ flex: 1, color: bgImage ? '#fff' : liveText, fontSize: 14 }}>{p.firstName} {p.lastName}</div>
                  <div style={{ fontWeight: 700, color: theme.accent }}>{p.score}</div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
